import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, LayoutDashboard, ChevronDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const Header = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const { advertiser, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const navLinks = [
        { name: 'Home', path: '/' },
        { name: 'Browse Listings', path: '/listings' },
        { name: 'About Us', path: '/about' },
        { name: 'Contact', path: '/contact' },
    ];

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
            <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                        <div className="w-10 h-10 bg-navy rounded-lg flex items-center justify-center">
                            <span className="text-gold font-bold text-xl">H</span>
                        </div>
                        <div className="hidden sm:block">
                            <span className="font-heading text-xl font-bold text-navy">Hotel Motel</span>
                            <span className="font-heading text-xl font-bold text-gold ml-1">Club</span>
                        </div>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-8">
                        {navLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
                                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                            >
                                {link.name}
                            </Link>
                        ))}
                    </div>

                    {/* Auth Buttons / User Menu */}
                    <div className="hidden md:flex items-center gap-4">
                        {advertiser ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" className="gap-2" data-testid="user-menu-trigger">
                                        <User className="w-4 h-4" />
                                        {advertiser.first_name}
                                        <ChevronDown className="w-4 h-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                    <DropdownMenuItem asChild>
                                        <Link to="/dashboard" className="flex items-center gap-2" data-testid="dashboard-link">
                                            <LayoutDashboard className="w-4 h-4" />
                                            Dashboard
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 text-red-600" data-testid="logout-btn">
                                        <LogOut className="w-4 h-4" />
                                        Logout
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <>
                                <Link to="/advertiser/login" data-testid="login-link">
                                    <Button variant="ghost">Advertiser Login</Button>
                                </Link>
                                <Link to="/advertiser/register" data-testid="register-link">
                                    <Button className="bg-gold hover:bg-amber-600 text-white">
                                        Post an Ad
                                    </Button>
                                </Link>
                            </>
                        )}
                    </div>

                    {/* Mobile menu button */}
                    <button
                        className="md:hidden p-2"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        data-testid="mobile-menu-btn"
                    >
                        {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>

                {/* Mobile Navigation */}
                {mobileMenuOpen && (
                    <div className="md:hidden py-4 border-t border-gray-100">
                        <div className="flex flex-col gap-2">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    className={`px-4 py-2 rounded-lg ${location.pathname === link.path ? 'bg-amber-50 text-amber-600' : 'text-gray-600'}`}
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {link.name}
                                </Link>
                            ))}
                            <div className="border-t border-gray-100 mt-2 pt-2">
                                {advertiser ? (
                                    <>
                                        <Link
                                            to="/dashboard"
                                            className="block px-4 py-2 text-gray-600"
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            Dashboard
                                        </Link>
                                        <button
                                            onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                                            className="block w-full text-left px-4 py-2 text-red-600"
                                        >
                                            Logout
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <Link
                                            to="/advertiser/login"
                                            className="block px-4 py-2 text-gray-600"
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            Advertiser Login
                                        </Link>
                                        <Link
                                            to="/advertiser/register"
                                            className="block px-4 py-2 text-amber-600 font-semibold"
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            Post an Ad
                                        </Link>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </nav>
        </header>
    );
};

export default Header;
